/* eslint-disable max-len */
import { Entity, SystemInterface } from '@goodboydigital/odie';
import { Container } from '@pixi/display';
import { Graphics } from '@pixi/graphics';
import p2 from 'p2';

import { Game } from '../Game';

const COLOR = {
    STATIC: 0xFF0000,
    DYNAMIC: 0xFFCC00,
    SENSOR: 0x0000FF,
};

export class PhysicsDrawSystem implements SystemInterface
{
    public game: Game;
    public innerContainer: Container;
    public view: Container;
    public graphs: Map<p2.Shape, Graphics>;

    constructor(entity: Entity)
    {
        this.game = entity.scene as Game;
        this.innerContainer = new Container();
        this.game.stage.addChild(this.innerContainer);
        this.view = new Container();
        this.innerContainer.addChild(this.view);
        this.view.alpha = 1;
        this.graphs = new Map();

        this.game.onPlay.connect(this.cleanup.bind(this));
        this.game.onIdle.connect(this.cleanup.bind(this));
    }

    public cleanup(): void
    {
        const items = Array.from(this.graphs.values());

        for (const item of items)
        {
            this.view.removeChild(item);
            item.destroy();
        }

        this.graphs.clear();
    }

    public postrender(): void
    {
        this.view.x = this.game.width / 2;
        this.view.y = this.game.height / 2;

        const bodies = this.game.physics.world.bodies;
        const camera = this.game.view25d.camera;
        const x = -camera.x;
        const y = -camera.y;

        this.view.x = x;
        this.view.y = y;
        this.innerContainer.scale.x = this.game.view25d.zoomContainer.scale.x;
        this.innerContainer.scale.y = this.game.view25d.zoomContainer.scale.y;

        this.graphs.forEach((graph) => { graph.renderable = false; });
        this.drawBodies(bodies);
    }

    protected drawBodies(bodies: p2.Body[]): void
    {
        for (const body of bodies)
        {
            for (const shape of body.shapes)
            {
                this.updateItem(body, shape);
            }
        }
    }

    public updateItem(body: p2.Body, shape: p2.Shape): void
    {
        if (!this.graphs.has(shape))
        {
            // eslint-disable-next-line no-nested-ternary
            const color = shape.sensor ? COLOR.SENSOR : (body.type === p2.Body.STATIC ? COLOR.STATIC : COLOR.DYNAMIC);
            const lineWidth = 1.5;
            const graph = new Graphics();

            if (shape.type === p2.Shape.CAPSULE)
            {
                const sh = shape as p2.Capsule;
                const w = sh.length + (sh.radius * 2);
                const h = sh.radius * 2;

                graph.beginFill(color, 0.1);
                graph.lineStyle({ color, width: lineWidth });
                graph.drawRoundedRect(-w / 2, -h / 2, w, h, sh.radius);
            }
            else if (shape.type === p2.Shape.CIRCLE)
            {
                const sh = shape as p2.Circle;

                graph.beginFill(color, 0.1);
                graph.lineStyle({ color, width: lineWidth });
                graph.drawCircle(0, 0, sh.radius);
                graph.moveTo(0, 0);
                graph.lineTo(sh.radius, 0);
            }
            else if (shape.type === p2.Shape.BOX)
            {
                const sh = shape as p2.Box;

                graph.beginFill(color, 0.1);
                graph.lineStyle({ color, width: lineWidth });
                graph.drawRect(-sh.width / 2, -sh.height / 2, sh.width, sh.height);
            }
            else if (shape.type === p2.Shape.CONVEX)
            {
                const sh = shape as p2.Convex;
                const verts = sh.vertices;

                graph.beginFill(color, 0.1);
                graph.lineStyle({ color, width: lineWidth });
                graph.moveTo(verts[0][0], -(verts[0][1]));

                for (let i = 1; i < verts.length; i += 1)
                {
                    graph.lineTo(verts[i][0], -(verts[i][1]));
                }
            }

            this.graphs.set(shape, graph);
            this.view.addChild(graph);
        }

        const graph = this.graphs.get(shape) as Graphics;

        graph.rotation = -body.angle;
        graph.x = body.position[0] + shape.position[0];
        graph.y = -body.position[1] + shape.position[1];
        graph.renderable = !!body.world;
    }
}
